import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Button,
  Paper,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { formatCurrency, reverseFormatCurrency } from 'helpers';
import {
  FeatureSubscriptionPaymentCycle,
  useFeatureSubscription_GetProductPriceQuery,
} from 'queries';

import { Plan } from 'pages/subscription-pricing-table-page';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

import TableHeaderPlan from './table-header-plan';

const useStyles = makeStyles((theme) => ({
  button: {
    borderRadius: '100px',
    border: `1px solid #1C2541`,
    backgroundColor: '#fff',
    color: '#1C2541',
  },
  isMostPopularButton: {
    border: `none`,
    backgroundColor: theme.palette.primary.main,
    color: 'white',
  },
  isMostPopularColumn: {
    background: `rgba(94, 209, 240, 0.10)`,
  },
  featureIndicator: {
    color: '#1C2541',
  },
  spacer: {
    margin: '16px 0px',
  },
  tableRow: {
    '&:nth-of-type(odd)': {
      backgroundColor: '#f2f2f2',
    },
  },
  tableCell: {
    '&&': {
      padding: '0px 0px 32px 0px',
    },
  },
  featureTableCell: {
    width: '45%',
  },
}));

type Props = {
  plans: Plan[];
  payCycle: FeatureSubscriptionPaymentCycle;
};

const PricingTable = ({ plans, payCycle }: Props) => {
  const classes = useStyles();
  const history = useHistory();
  const { portalId } = useParams<Record<string, string>>();
  const [prices, setPrices] = useState<Record<string, string>>({});

  // const features = Object.keys(plans[0].features);

  const handleOnBuyClick = (productCode: string) => {
    const queryString = `?productCode=${productCode}&payCycle=${payCycle}`;

    history.push(`/home/${portalId}/subscription-checkout${queryString}`);
  };

  const getSubscribeButtonText = (planName: string) => {
    const buttonText = planName === 'Trial' ? 'Start' : 'Buy';
    return buttonText + ` ${planName}`;
  };

  const { refetch: refetchProductPrice } = useFeatureSubscription_GetProductPriceQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      input: {
        portalId,
        payCycle: payCycle,
        productCode: plans[0].priceIdKey,
      },
    },
    onError: (e) => {
      showAlert({
        message: 'Error getting product price: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  useEffect(() => {
    const fetchPrices = async () => {
      const updatedPrices: Record<string, string> = {};

      for (const plan of plans) {
        const response = await refetchProductPrice({
          input: {
            portalId,
            payCycle: payCycle,
            productCode: plan.priceIdKey,
          },
        });

        if (response.data) {
          const price =
            payCycle === FeatureSubscriptionPaymentCycle.Yearly
              ? response.data.data.annualPrice!
              : response.data.data.monthlyPrice!;

          updatedPrices[plan.priceIdKey] =
            payCycle === FeatureSubscriptionPaymentCycle.Yearly
              ? formatCurrency(`${reverseFormatCurrency(price) / 12}`, true)
              : formatCurrency(`${reverseFormatCurrency(price)}`, true);
        }
      }

      setPrices(updatedPrices);
    };

    fetchPrices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payCycle]);

  return (
    <TableContainer component={Paper}>
      <Table aria-label="simple table">
        <TableHead>
          <TableRow style={{ verticalAlign: 'bottom' }}>
            {/* <TableCell className={classes.featureTableCell}>Features</TableCell> */}
            {plans.map((plan) => (
              <TableCell
                key={plan.id}
                align="center"
                className={clsx(classes.tableCell, {
                  [classes.isMostPopularColumn]: plan.isMostPopular,
                })}
              >
                <TableHeaderPlan
                  plan={plan.name}
                  isMostPopular={plan.isMostPopular}
                  price={prices[plan.priceIdKey]}
                />
                <div className={classes.spacer} />
                <Button
                  className={clsx(classes.button, {
                    [classes.isMostPopularButton]: plan.isMostPopular,
                  })}
                  variant="contained"
                  size="medium"
                  color="primary"
                  onClick={() => handleOnBuyClick(plan.priceIdKey)}
                >
                  {getSubscribeButtonText(plan.name)}
                </Button>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        {/* <TableBody>
          {features.map((feature) => (
            <TableRow key={feature} className={classes.tableRow}>
              <TableCell component="th" scope="row">
                {feature}
              </TableCell>
              {plans.map((plan) => (
                <TableCell
                  key={plan.id}
                  align="center"
                  className={clsx({
                    [classes.isMostPopularColumn]: plan.isMostPopular,
                  })}
                >
                  <span className={classes.featureIndicator}>
                    {typeof plan.features[feature] !== 'boolean'
                      ? plan.features[feature]
                      : plan.features[feature] === true
                        ? '✓'
                        : '—'}
                  </span>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody> */}
      </Table>
    </TableContainer>
  );
};

export default PricingTable;
